import React, {useEffect, useState} from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_BASE_URL;
const PAGE_SIZE = 20;

const TYPES = {
  QuotationNew: {
    label: 'Quotations',
    icon: '🧾',
    gradient: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
    endpoint: '/quotation-new/all',
    summary: true,
  },
  TallyQuotation: {
    label: 'Tally Quotations',
    icon: '📒',
    gradient: 'linear-gradient(135deg, #0891B2 0%, #0EA5E9 100%)',
    endpoint: '/tally-quotation/all',
    summary: false,
  },
};

const recipientName = q =>
  q?.recipient?.name ||
  q?.recipient?.businessName ||
  q?.recipient?.firmName ||
  'Quotation';

/**
 * Reusable quotation picker modal, styled after MasterSelection.
 * Uses server-side search + pagination across QuotationNew + TallyQuotation.
 *
 * Props:
 *  - isOpen, onClose
 *  - onSelect(quotation) where quotation = {_id, id, type, recipientName}
 *  - companyId
 *  - title, initialType
 */
const QuotationSelection = ({
  isOpen,
  onClose,
  onSelect,
  companyId,
  title = 'Select Quotation',
  initialType = 'QuotationNew',
}) => {
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem('darkMode') === 'true',
  );
  const [activeTab, setActiveTab] = useState(initialType);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [quotations, setQuotations] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab(TYPES[initialType] ? initialType : 'QuotationNew');
    setSearch('');
    setDebouncedSearch('');
    setPage(1);
  }, [isOpen, companyId, initialType]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!isOpen || !companyId) return;
    let cancelled = false;
    const cfg = TYPES[activeTab];
    const load = async () => {
      setLoading(true);
      try {
        const today = new Date().toISOString().slice(0, 10);
        const {data} = await axios.get(`${API}${cfg.endpoint}`, {
          params: {
            companyId,
            page,
            limit: PAGE_SIZE,
            search: debouncedSearch || undefined,
            startDate: '2000-01-01',
            endDate: today,
            summary: cfg.summary ? true : undefined,
          },
        });
        if (cancelled) return;
        setQuotations(data?.quotations || []);
        setCount(data?.count || 0);
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading quotations:', error);
        setQuotations([]);
        setCount(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, companyId, activeTab, page, debouncedSearch]);

  if (!isOpen) return null;

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const cardCls = darkMode
    ? 'bg-gray-800 border-gray-700'
    : 'bg-white border-gray-200';
  const mutedCls = darkMode ? 'text-gray-400' : 'text-gray-500';

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div
          className={`relative w-full max-w-3xl max-h-[90vh] flex flex-col rounded-2xl overflow-hidden border-2 shadow-2xl ${cardCls}`}
          onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div
            className="flex-shrink-0 px-6 py-4 flex items-center justify-between"
            style={{background: TYPES[activeTab].gradient}}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center text-xl">
                {TYPES[activeTab].icon}
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">{title}</h2>
                <p className="text-xs text-white/70">
                  Search and pick a quotation to link
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2.5 rounded-xl bg-white/10 hover:bg-white/25 text-white transition-colors"
              aria-label="Close">
              ✕
            </button>
          </div>

          {/* Tabs */}
          <div
            className={`flex-shrink-0 flex gap-1 px-4 py-2 border-b ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
            {Object.keys(TYPES).map(type => {
              const active = activeTab === type;
              return (
                <button
                  key={type}
                  onClick={() => {
                    setActiveTab(type);
                    setSearch('');
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                    active
                      ? 'text-white shadow'
                      : darkMode
                      ? 'text-gray-400 hover:bg-gray-700'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                  style={active ? {background: TYPES[type].gradient} : {}}>
                  <span>{TYPES[type].icon}</span>
                  <span>{TYPES[type].label}</span>
                  {active ? <span className="opacity-70">({count})</span> : null}
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div
            className={`flex-shrink-0 px-6 py-3 border-b ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by quotation no. or recipient name..."
              className={`w-full border-2 rounded-xl px-4 py-2.5 text-sm transition-colors focus:outline-none ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-indigo-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-indigo-500'
              }`}
            />
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <p className={`text-sm py-10 text-center ${mutedCls}`}>
                Loading quotations…
              </p>
            ) : quotations.length === 0 ? (
              <p className={`text-sm py-10 text-center ${mutedCls}`}>
                No quotations found
              </p>
            ) : (
              <div className="space-y-2">
                {quotations.map(q => (
                  <button
                    key={q._id}
                    type="button"
                    onClick={() => {
                      onSelect({
                        _id: q._id,
                        id: q.id,
                        type: activeTab,
                        recipientName: recipientName(q),
                      });
                      onClose();
                    }}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all hover:shadow-md ${
                      darkMode
                        ? 'border-gray-700 hover:border-indigo-500 hover:bg-gray-700/50'
                        : 'border-gray-200 hover:border-indigo-400 hover:bg-indigo-50'
                    }`}>
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0"
                      style={{background: TYPES[activeTab].gradient}}>
                      {(q.id || 'Q').slice(-2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className={`font-bold truncate ${
                          darkMode ? 'text-gray-100' : 'text-gray-800'
                        }`}>
                        {q.id || 'Quotation'}
                      </div>
                      <div className={`text-xs truncate ${mutedCls}`}>
                        {recipientName(q)}
                        {q.status ? ` · ${q.status}` : ''}
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-right text-xs">
                      <div
                        className={`font-semibold ${
                          darkMode ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                        {q.totalAmount != null
                          ? `₹${Number(q.totalAmount).toLocaleString('en-IN')}`
                          : ''}
                      </div>
                      <div className={mutedCls}>
                        {q.createdAt
                          ? new Date(q.createdAt).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })
                          : ''}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          <div
            className={`flex-shrink-0 flex items-center justify-between px-6 py-3 border-t ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
            <span className={`text-xs ${mutedCls}`}>
              {count} quotation{count === 1 ? '' : 's'}
            </span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  page <= 1 || loading
                    ? 'opacity-40 cursor-not-allowed'
                    : darkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-100'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}>
                Prev
              </button>
              <span className={`text-xs ${mutedCls}`}>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  page >= totalPages || loading
                    ? 'opacity-40 cursor-not-allowed'
                    : darkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-100'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }`}>
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationSelection;
